import React, {useContext, useState} from 'react';
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Input from "@amzn/meridian/input";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {injectIntl} from "react-intl";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Alert from "@amzn/meridian/alert";
import Select, {SelectOption} from "@amzn/meridian/select";
import {CONFIG_TYPE, ZANCA_SORT_TIER_NAMES} from "../../../util/Constants";
import {DraftContext} from "../../../draftSave/context/DraftSaveContext";
import {getLocalizedMessage} from "../../../util/Localization";
import {validateZancaSortTierConfig, checkTierConfigLevelOverlap} from "../../../util/Validations";

const AddZancaSortTierConfigModal = (props) => {
    const {mac, intl} = props;
    const {getDraftData, addInstance, editInstance} = useContext(DraftContext);

    // Visual controls
    const {open, setOpen} = props;
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [edit, setEdit] = useState(false);

    // Values
    const [tierName, setTierName] = useState("");
    const [capacity, setCapacity] = useState("");
    const [startLevel, setStartLevel] = useState("");
    const [endLevel, setEndLevel] = useState("");

    // Error states
    const [tierNameErr, setTierNameErr] = useState(false);
    const [capacityErr, setCapacityErr] = useState(false);
    const [startLevelErr, setStartLevelErr] = useState(false);
    const [endLevelErr, setEndLevelErr] = useState(false);

    const onCloseModal = () => {
        setShowError(false);
        setErrorMessage("");
        setOpen(false);
        resetInputState();
    };

    const getExistingTierConfigs = () => {
        return getDraftData({
            stateKey: CONFIG_TYPE.ZANCA_SORT_TIER_CONFIGURATION,
            subKey: mac
        });
    };

    const buildTierConfig = () => {
        return {
            tierName: tierName,
            capacity: parseInt(capacity, 10),
            startLevel: parseInt(startLevel, 10),
            endLevel: parseInt(endLevel, 10)
        };
    };

    const checkValidations = () => {
        const config = buildTierConfig();

        if (!validateZancaSortTierConfig(config)) {
            setErrorMessage("Please enter all fields in correct format.");
            return false;
        }

        // Check overlap against existing tiers (excluding current tier if editing)
        const existingTiers = getExistingTierConfigs().filter(
            t => t.tierName !== config.tierName
        );
        const allTiers = [...existingTiers, config];
        const overlap = checkTierConfigLevelOverlap(allTiers);
        if (overlap) {
            setErrorMessage(
                `Level overlap detected between ${overlap.tier1} and ${overlap.tier2}.`
            );
            return false;
        }

        return true;
    };

    const prefill = (selectedTierName) => {
        const existing = getExistingTierConfigs();
        const match = existing.find(t => t.tierName === selectedTierName);
        if (match) {
            setEdit(true);
            setCapacity(String(match.capacity));
            setStartLevel(String(match.startLevel));
            setEndLevel(String(match.endLevel));
            setCapacityErr(false);
            setStartLevelErr(false);
            setEndLevelErr(false);
        } else if (edit) {
            setEdit(false);
            setCapacity("");
            setStartLevel("");
            setEndLevel("");
        }
    };

    const onClickConfirm = () => {
        if (checkValidations()) {
            const config = buildTierConfig();
            const existingTiers = getExistingTierConfigs();
            const isEdit = existingTiers.some(t => t.tierName === config.tierName);
            setConfigData(config, isEdit);
            setOpen(false);
            resetInputState();
            setShowError(false);
            setErrorMessage("");
        } else {
            setShowError(true);
        }
    };

    const setConfigData = (config, isEdit) => {
        if (isEdit) {
            editInstance({
                stateKey: CONFIG_TYPE.ZANCA_SORT_TIER_CONFIGURATION,
                subKey: mac,
                instance: config,
                keyComparator: (config1, config2) => (
                    config1.tierName === config2.tierName
                ),
                valueComparator: (config1, config2) => (
                    config1.capacity === config2.capacity &&
                    config1.startLevel === config2.startLevel &&
                    config1.endLevel === config2.endLevel
                )
            });
        } else {
            addInstance({
                stateKey: CONFIG_TYPE.ZANCA_SORT_TIER_CONFIGURATION,
                subKey: mac,
                instance: config
            });
        }
    };

    function resetInputState() {
        setTierName("");
        setCapacity("");
        setStartLevel("");
        setEndLevel("");
        setTierNameErr(false);
        setCapacityErr(false);
        setStartLevelErr(false);
        setEndLevelErr(false);
        setEdit(false);
    }

    return (
        <Row>
            <Modal
                title={getLocalizedMessage({intl, stringId: "tierconfig.addupdate"})}
                open={open}
                onClose={onCloseModal}
                scrollContainer="viewport"
                closeLabel="Close"
                bodySpacingInset="medium">
                <Column spacing={"medium"} spacingInset={"medium"}>
                    <Column spacing="200">
                        <b>
                            {getLocalizedMessage({intl, stringId: "modal.addtierheading"})}
                        </b>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="tier-name-select">
                            {getLocalizedMessage({intl, stringId: "tierconfig.tierName"})}
                        </Text>
                        <Select
                            id="tier-name-select"
                            value={tierName}
                            onChange={(selectedTierName) => {
                                setTierName(selectedTierName);
                                setTierNameErr(!selectedTierName);
                                prefill(selectedTierName);
                            }}
                            error={tierNameErr}
                            placeholder={getLocalizedMessage({intl, stringId: "tierconfig.selectTier"})}
                            width="100%">
                            {ZANCA_SORT_TIER_NAMES.map((name) =>
                                <SelectOption key={name} value={name} label={name}/>
                            )}
                        </Select>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="capacity-input">
                            {getLocalizedMessage({intl, stringId: "tierconfig.capacity"})}
                        </Text>
                        <Input
                            id="capacity-input"
                            value={capacity}
                            onChange={setCapacity}
                            onBlur={() => {
                                const val = parseInt(capacity, 10);
                                setCapacityErr(!Number.isInteger(val) || val <= 0);
                            }}
                            error={capacityErr}
                            type="number"
                            placeholder="e.g. 50"
                        />
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="start-level-input">
                            {getLocalizedMessage({intl, stringId: "tierconfig.startLevel"})}
                        </Text>
                        <Input
                            id="start-level-input"
                            value={startLevel}
                            onChange={setStartLevel}
                            onBlur={() => {
                                const val = parseInt(startLevel, 10);
                                setStartLevelErr(!Number.isInteger(val) || val < 1);
                            }}
                            error={startLevelErr}
                            type="number"
                            placeholder="e.g. 1"
                        />
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="end-level-input">
                            {getLocalizedMessage({intl, stringId: "tierconfig.endLevel"})}
                        </Text>
                        <Input
                            id="end-level-input"
                            value={endLevel}
                            onChange={setEndLevel}
                            onBlur={() => {
                                const val = parseInt(endLevel, 10);
                                setEndLevelErr(!Number.isInteger(val) || val < 1);
                            }}
                            error={endLevelErr}
                            type="number"
                            placeholder="e.g. 5"
                        />
                    </Column>
                </Column>
                <ModalFooter>
                    <Column>
                        {showError &&
                            <Alert type="warning" size="small">
                                {errorMessage || getLocalizedMessage({intl, stringId: "alert.missingfields"})}
                            </Alert>
                        }
                    </Column>
                    <Row alignmentHorizontal="right" widths="fit">
                        <Button type="secondary" size="medium" onClick={onCloseModal}>
                            {getLocalizedMessage({intl, stringId: "modal.cancel"})}
                        </Button>
                        <Button type="primary" size="medium" onClick={onClickConfirm}>
                            {edit
                                ? getLocalizedMessage({intl, stringId: "modal.confirmUpdate"})
                                : getLocalizedMessage({intl, stringId: "modal.confirmAdd"})
                            }
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </Row>
    );
};

export default injectIntl(AddZancaSortTierConfigModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/zancasort/modal/AddZancaSortTierConfigModal.js